import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ShoppingBag, AlertCircle, Sparkles, MapPin, Layout, Gift,
  TrendingUp, Users, Footprints, Check, Megaphone
} from "lucide-react";
import { FootTrafficAuditForm } from "../../components/FootTrafficAuditForm";
import { SEO } from "../../components/SEO";

const RetailSolutionPage = () => {
  const navigate = useNavigate();
  return (
    <div className="pt-20" data-testid="solution-retail-page">
      <SEO
        title="SmartReach™ for Retail"
        description="Drive in-store retail foot traffic with SmartReach™ — DOOH, mobile geofencing, and loyalty rewards that win nearby shoppers and lift in-store conversion."
        path="/solutions/retail"
      />

      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <ShoppingBag size={16} /> Retail Stores
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            Win Back The <span className="text-gradient-orange">In-Store Shopper</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            SmartReach&trade; for retail brings nearby shoppers off their phones and into your stores —
            during launches, sales, and the slow weeks in between.
          </p>
          <button onClick={() => navigate("/contact")} className="btn-primary text-lg">
            Drive Store Visits <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The Retail <span className="text-gradient-orange">Challenges</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Declining In-Store Visits", desc: "Customers research and shop online by default. Without a real reason to walk in, they don't." },
              { title: "Online Competition", desc: "Marketplaces and DTC brands compete for the same wallet — but your physical store is the differentiator." }
            ].map((c) => (
              <div key={c.title} className="bg-gradient-to-br from-red-50 to-white p-6 rounded-2xl border border-red-100">
                <AlertCircle className="text-red-500 mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
                <p className="text-gray-medium text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-white px-3 py-1 rounded-full shadow-sm">
              <Sparkles size={14} /> SmartReach Solution
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The Retail <span className="text-gradient-orange">Foot-Traffic Stack</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "Location Targeting", desc: "Reach shoppers within driving distance, near competitor locations, or in-mall during peak hours." },
              { icon: Layout, title: "DOOH + Digital Mix", desc: "Out-of-home billboards and digital screens reinforce mobile and social — multiple touchpoints, one journey." },
              { icon: Gift, title: "Promotions + Rewards", desc: "Time-limited offers and loyalty point boosts create urgency and bring nearby shoppers into the store." }
            ].map((s) => (
              <div key={s.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <s.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{s.title}</h3>
                <p className="text-gray-medium text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">Use Cases</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                title: "Store Launches",
                desc: "30-day launch campaign: DOOH billboards within 5 miles + mobile geofencing + radio creates buzz and drives opening-week traffic.",
                bullets: ["DOOH within 5-mile radius", "Geofenced mobile push", "Opening-week loyalty bonuses"]
              },
              {
                title: "Seasonal Sales",
                desc: "Friday-Sunday weekend pushes for back-to-school, holiday, and clearance moments. Combined channels lift visits and basket size.",
                bullets: ["Daypart-targeted offers", "Loyalty point multipliers", "Cross-channel reinforcement"]
              }
            ].map((u) => (
              <div key={u.title} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover">
                <Megaphone className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-2">{u.title}</h3>
                <p className="text-gray-medium mb-4">{u.desc}</p>
                <div className="space-y-2 pt-4 border-t border-orange-primary/10">
                  {u.bullets.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <Check size={16} className="text-orange-primary flex-shrink-0" />
                      <span className="text-sm text-gray-dark">{b}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Results</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Footprints, title: "Increased Store Traffic", desc: "Measurable lift in real walk-ins — not just clicks or impressions." },
              { icon: TrendingUp, title: "Higher Conversion", desc: "Local shoppers with offers convert at higher rates than broad-targeted online ads." },
              { icon: Users, title: "Stronger Local Brand", desc: "Repeated cross-channel exposure builds local equity that compounds over time." }
            ].map((r) => (
              <div key={r.title} className="bg-white p-6 rounded-2xl shadow-card card-hover">
                <r.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{r.title}</h3>
                <p className="text-gray-medium text-sm">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FootTrafficAuditForm
        title="Get a retail foot-traffic plan in 24 hours"
        subtitle="Tell us about your stores. We'll send back a SmartReach™ plan tailored for your category and locations."
        variant="dark"
      />
    </div>
  );
};

export default RetailSolutionPage;
